import React, { useState, useEffect } from 'react';
import { FiX, FiUsers, FiPlus } from 'react-icons/fi';

const TeamDetailModal = ({ isOpen, onClose, team, token, onUpdate }) => {
  const [users, setUsers] = useState([]);
  const [boards, setBoards] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(team || {});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    try {
      const [usersRes, boardsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/boards`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users || []);
      }
      if (boardsRes.ok) {
        const data = await boardsRes.json();
        setBoards(data.boards || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  if (!isOpen || !team) return null;

  const handleUpdate = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/teams/${team.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          members: formData.members,
          boards: formData.boards
        })
      });
      
      if (response.ok) {
        onUpdate();
        setEditMode(false);
      }
    } catch (error) {
      console.error('Failed to update team:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleMember = (userId) => {
    const members = formData.members || [];
    const newMembers = members.includes(userId)
      ? members.filter(id => id !== userId)
      : [...members, userId];
    setFormData({ ...formData, members: newMembers });
  };

  const toggleBoard = (boardId) => {
    const teamBoards = formData.boards || [];
    const newBoards = teamBoards.includes(boardId)
      ? teamBoards.filter(id => id !== boardId)
      : [...teamBoards, boardId];
    setFormData({ ...formData, boards: newBoards });
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '700px' }}>
        <div className="modal-header">
          <h2 className="modal-title">
            <FiUsers size={24} style={{ marginRight: '12px' }} />
            {editMode ? 'Edit Team' : team.name}
          </h2>
          <button className="modal-close" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>
        <div className="modal-body">
          {editMode ? (
            <>
              <div className="form-group">
                <label className="form-label">Team Name</label>
                <input
                  type="text"
                  className="input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="input"
                  rows="2"
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Team Members</label>
                <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid var(--border-light)', borderRadius: '4px', padding: '8px' }}>
                  {users.map(user => (
                    <label key={user.id} style={{ display: 'flex', alignItems: 'center', padding: '6px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={(formData.members || []).includes(user.id)}
                        onChange={() => toggleMember(user.id)}
                        style={{ marginRight: '8px' }}
                      />
                      <div className="avatar avatar-sm" style={{ marginRight: '8px' }}>
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                      <span>{user.name} ({user.role})</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Assigned Boards</label>
                <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid var(--border-light)', borderRadius: '4px', padding: '8px' }}>
                  {boards.map(board => (
                    <label key={board.id} style={{ display: 'flex', alignItems: 'center', padding: '6px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={(formData.boards || []).includes(board.id)}
                        onChange={() => toggleBoard(board.id)}
                        style={{ marginRight: '8px' }}
                      />
                      <span>{board.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">Description</label>
                <div>{team.description || 'No description'}</div>
              </div>
              <div className="form-group">
                <label className="form-label">Members ({team.members?.length || 0})</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                  {(team.members || []).map((memberId) => {
                    const user = users.find(u => u.id === memberId);
                    return user ? (
                      <div key={memberId} className="avatar" title={user.name}>
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Assigned Boards ({team.boards?.length || 0})</label>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                  {(team.boards || []).length === 0 ? 'No boards assigned' : `${team.boards.length} board(s) assigned`}
                </div>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          {editMode ? (
            <>
              <button className="btn btn-secondary" onClick={() => { setEditMode(false); setFormData(team); }} disabled={loading}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleUpdate} disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
              <button className="btn btn-primary" onClick={() => setEditMode(true)}>
                Edit Team
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamDetailModal;